import * as React from 'react';
import classnames from 'classnames';
import { useRefinementList, useMenu } from 'react-instantsearch';
import { IsRefinableItem } from '../helpers';

function CustomRefinementList({attribute, initialFacetValues, isFirstFacet}) {
  const {items: allItems, refine} = useRefinementList({attribute});
  const allItemsRef = React.useRef(new Map());
  const initializedRef = React.useRef(false);
  const refinableItems = allItems.map(item => item.value);

  // Initialize with all facet values from bootstrap on first render
  React.useEffect(() => {
    if (initialFacetValues && initialFacetValues.length > 0 && !initializedRef.current) {
      initialFacetValues.forEach(facetValue => {
        allItemsRef.current.set(facetValue.value, {
          value: facetValue.value,
          label: facetValue.value,
          count: facetValue.count,
          isRefined: false,
        });
      });
      initializedRef.current = true;
    }
  }, [initialFacetValues]);

  // Auto-deselect refined items that no longer have data (subsequent facets only)
  React.useEffect(() => {
    if (isFirstFacet) return;

    allItems.forEach(item => {
      if (item.isRefined && item.count === 0) {
        refine(item.value);
      }
    });
  }, [allItems, isFirstFacet, refine]);

  // Update items with current state from InstantSearch
  // Reset isRefined on all entries first to clear stale state from items
  // that dropped out of useRefinementList's results after deselection.
  allItemsRef.current.forEach((entry, key) => {
    allItemsRef.current.set(key, {...entry, isRefined: false});
  });
  allItems.forEach(item => {
    allItemsRef.current.set(item.value, item);
  });

  const handleClick = (value) => refine(value);

  return (
    <ul>
      {Array.from(allItemsRef.current.values())
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => {
          const isRefinable = isFirstFacet || IsRefinableItem(refinableItems, item);
          const isChecked = isFirstFacet ? item.isRefined : (item.isRefined && item.count > 0);

          return (
            <li key={item.value} className={!isRefinable ? 'facet-empty' : 'with-value'}>
              <label>
                <input
                  type="checkbox"
                  checked={isChecked}
                  data-checked={isChecked}
                  onChange={() => handleClick(item.value)}
                  disabled={!isRefinable}
                />
                <span>{item.label}</span>
                <span className='ais-Menu-count'> ({item.count})</span>
              </label>
            </li>
          );
        })}
    </ul>
  );
}

function CustomMenu({attribute, initialFacetValues, isFirstFacet}) {
  const {items, refine} = useMenu({attribute});
  const allItemsRef = React.useRef(new Map());
  const initializedRef = React.useRef(false);

  const refinableItems = items.map(item => item.value);

  // Initialize with all facet values from bootstrap on first render
  React.useEffect(() => {
    if (initialFacetValues && initialFacetValues.length > 0 && !initializedRef.current) {
      initialFacetValues.forEach(facetValue => {
        allItemsRef.current.set(facetValue.value, {
          value: facetValue.value,
          label: facetValue.value,
          count: facetValue.count,
          isRefined: false,
        });
      });
      initializedRef.current = true;
    }
  }, [initialFacetValues]);

  // Auto-update selection when current selection becomes invalid (subsequent facets only)
  // Two-phase approach: first deselect invalid, then on next render select first valid
  React.useEffect(() => {
    if (isFirstFacet) return;
    if (items.length === 0) return;

    const refinedItem = items.find(i => i.isRefined);

    if (refinedItem && refinedItem.count === 0) {
      // Phase 1: deselect the invalid selection to clear this filter,
      // which allows upstream counts to recalculate on next render
      refine(refinedItem.value);
      return;
    }

    if (!refinedItem) {
      // Phase 2: no selection active — pick the first valid option
      const firstValid = items.find(i => i.count > 0);
      if (firstValid) {
        refine(firstValid.value);
      }
    }
  }, [items, isFirstFacet, refine]);

  // Update items with current state from InstantSearch
  // Reset isRefined on all entries first, then apply current state.
  // Items that drop out of useMenu's results (e.g. after deselection)
  // would otherwise retain stale isRefined: true in the ref.
  allItemsRef.current.forEach((entry, key) => {
    allItemsRef.current.set(key, {...entry, isRefined: false});
  });
  items.forEach(item => allItemsRef.current.set(item.value, item));

  const handleClick = (value) => refine(value);

  return (
    <ul>
      {Array.from(allItemsRef.current.values())
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => {
          const isRefinable = isFirstFacet || IsRefinableItem(refinableItems, item);

          return (
            <li key={item.value} className={!isRefinable ? 'facet-empty' : 'with-value'}>
              <label>
                <input
                  type="radio"
                  name={attribute}
                  data-checked={item.isRefined}
                  checked={item.isRefined}
                  onChange={() => handleClick(item.value)}
                  disabled={!isRefinable}
                />
                <span>{item.label}</span>
                <span className='ais-Menu-count'> ({item.count})</span>
              </label>
            </li>
          );
        })}
    </ul>
  );
}

function CustomDropdown({ attribute, initialFacetValues, isFirstFacet }) {
  const { items: searchItems, refine } = useMenu({ attribute });
  const facetMapRef = React.useRef(new Map());
  const isInitializedRef = React.useRef(false);

  React.useEffect(() => {
    const shouldInitialize = initialFacetValues?.length > 0 && !isInitializedRef.current;
    if (!shouldInitialize) return;

    initialFacetValues.forEach(({ value, count }) => {
      facetMapRef.current.set(value, {
        value,
        label: value,
        count,
        isRefined: false,
      });
    });
    isInitializedRef.current = true;
  }, [initialFacetValues]);

  // Auto-update selection when current selection becomes invalid (subsequent facets only)
  React.useEffect(() => {
    if (isFirstFacet) return;
    if (searchItems.length === 0) return;

    const refinedItem = searchItems.find(i => i.isRefined);

    if (refinedItem && refinedItem.count === 0) {
      refine(refinedItem.value);
      return;
    }

    if (!refinedItem) {
      const firstValid = searchItems.find(i => i.count > 0);
      if (firstValid) {
        refine(firstValid.value);
      }
    }
  }, [searchItems, isFirstFacet, refine]);

  // Reset isRefined on all entries first to clear stale state,
  // then apply current InstantSearch items.
  facetMapRef.current.forEach((entry, key) => {
    facetMapRef.current.set(key, {...entry, isRefined: false});
  });
  searchItems.forEach(item => facetMapRef.current.set(item.value, item));

  const allItems = Array.from(facetMapRef.current.values());
  const sortedItems = allItems.sort((a, b) => a.value.localeCompare(b.value));
  const refinableValues = new Set(searchItems.map(item => item.value));
  const selectedItem = sortedItems.find(item => item.isRefined);

  const containerRef = React.useRef(null);
  const toggleDropdown = () => {
    containerRef.current.classList.toggle('group-selector--expanded');
  };

  const handleSelect = (value) => {
    refine(value);
    toggleDropdown();
  };

  return (
    <div className="group-selector" ref={containerRef}>
      <div className="group-selector__selected">
        <button type="button" onClick={toggleDropdown}>
          {selectedItem?.label || 'Select...'}
          <div className="transition-transform rotate-0">
            <svg className="size-[24px]" fill="none" viewBox="0 0 24 24">
              <path d="M7 10l5 5 5-5" stroke="#666666" strokeWidth="2" strokeLinecap="round"
                    strokeLinejoin="round"></path>
            </svg>
          </div>
        </button>
      </div>
      <div className="group-selector__items">
        {sortedItems.map(item => {
          const isDisabled = !isFirstFacet && !refinableValues.has(item.value);

          return (
            <div className={classnames('group-selector__item', {
              'group-selector__item--selected': item.isRefined,
            })} key={item.value}>
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => handleSelect(item.value)}
              >
                {item.label}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterWrapper(props) {
  const {label, children, kind, attribute} = props;
  const classNames = ['filter-wrapper', `filter-wrapper--${kind}`];
  return (
    <details className={classNames.join(' ')} data-attribute={attribute} data-kind={kind} open={true}>
      <summary>
        <strong>{label}</strong>
        <div className="filter-wrapper__arrow"></div>
      </summary>
      <div className='filter-wrapper__content'>
        {children}
      </div>
    </details>
  );
}

function CheckboxesFilter({label, attribute, initialFacetValues, isFirstFacet}) {
  return (
    <FilterWrapper label={label} attribute={attribute} kind='checkboxes'>
      <CustomRefinementList attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

function RadioButtonsFilter({label, attribute, initialFacetValues, isFirstFacet}) {
  return (
    <FilterWrapper label={label} attribute={attribute} kind='radio-buttons'>
      <CustomMenu attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

function DropdownFilter({label, attribute, initialFacetValues, isFirstFacet}) {
  return (
    <FilterWrapper label={label} attribute={attribute} kind='dropdown'>
      <CustomDropdown attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>
    </FilterWrapper>
  );
}

export default function Filter(props) {
  const {config, facetCounts, isFirstFacet} = props;
  const {
    attribute = null,
    label,
    kind,
  } = config;

  if (!attribute) {
    return null;
  }

  // Extract initial facet values for this attribute from facetCounts
  const initialFacetValues = facetCounts?.find(fc => fc.field_name === attribute)?.counts || [];

  switch (kind) {
    case 'radio':
      return <RadioButtonsFilter label={label} attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>;
    case 'checkboxes':
      return <CheckboxesFilter label={label} attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>;
    case 'dropdown':
      return <DropdownFilter label={label} attribute={attribute} initialFacetValues={initialFacetValues} isFirstFacet={isFirstFacet}/>;
    default:
      return null;
  }
}
